import { PaginatedDataDTO } from "@bottega52/commons-pagination";
import { Button, Card } from "@material-ui/core";
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import DomainsIcon from '@material-ui/icons/Domain';
import WarningIcon from '@material-ui/icons/Warning';
import * as React from "react";
import { ConnectedProps, connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as CustomerPlantDecoder from '../../codec/customerDomainDecoder';
import * as CreditsActions from '../../redux/credits/credits.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import * as SettingsActions from '../../redux/settings/settings.actions';
import { IState } from '../../redux/store';
import { ICustomerInDTO } from "../../repository/jago/model/input/ICustomerInDTO";
import { ICustomerDomainFormDTO, IDomainInDTO } from "../../repository/jago/model/input/IDomainInDTO";
import { IWalletInDTO } from "../../repository/jago/model/input/IWalletInDTO";
import AbilityProvider from "../../services/PermissionService/AbilityProvider";
import { PERMISSIONS } from "../../services/PermissionService/PermissionConstants";
import CustomerPlantForm from "../Forms/CustomerPlantForm";
import { ModalTypes } from "../Modals/ModalTypes";
import ReduxLanguage from "../ReduxLanguage";
import CustomerDomainsTable from "./CustomerDomainsTable";

const styles = createStyles({
  container: {
    display: 'flex',
    marginTop: 20,
    flexDirection: 'column',
  },
  cardContainer: {
    padding: 10,
    borderLeft: '3px solid #5AC0B1',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerInner: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTextContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  icon: {
    marginRight: 5,
    width: 25,
  },
  title: {
    fontWeight: 'bold'
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 380px)',
    width: '100%',
    marginTop: 10
  }
});

export interface ICustomerPlantsSectionState {
  pageSize: number;
}

type IReduxProps = ConnectedProps<typeof connector> & RouteComponentProps<any>;

export interface ICustomerPlantsSectionProps extends WithStyles<typeof styles> {
  language: string;
  customer: ICustomerInDTO;
  onShowWalletDetails: (wallet: IWalletInDTO) => void;
}

export type ComponentProps = ICustomerPlantsSectionProps & IReduxProps;

class CustomerPlantsSection extends React.Component<ComponentProps, ICustomerPlantsSectionState> {

  constructor(props: ComponentProps) {
    super(props);
    this.state  = {
      pageSize: 100
    };
  }

  async onOpenEditCustomerPlantForm(customerPlant: IDomainInDTO) {
    const { dispatch } = this.props;
    if (customerPlant.solution === 'LUCKEY' && customerPlant.management === 'SAAS')
      return;
    const customerDomainFormData = CustomerPlantDecoder.decodeCustomerPlantContentToForm(customerPlant);
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_DOMAIN_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: (
          <CustomerPlantForm
            customerId={customerDomainFormData.customerId}
            customerPlant={customerDomainFormData}
            onCreateEditCustomerPlant={(newCustomerPlantData: ICustomerDomainFormDTO) => this.onEditCustomerPlantRequest(customerPlant.id, newCustomerPlantData)}
          />
        ),
        titleMessageKey: 'credits.customerDomainFormTitle',
      }
    }));
  }

  async onOpenNewCustomerPlantForm() {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`OPERATIONAL_CUSTOMER_DOMAIN_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL_BACKDROP,
      modalProps: {
        content: (
          <CustomerPlantForm
            customerId={this.props.customer.id}
            onCreateEditCustomerPlant={(newCustomerPlantData: ICustomerDomainFormDTO) => this.onCreateNewCustomerPlantRequest(newCustomerPlantData)}
          />
        ),
        titleMessageKey: 'credits.customerDomainFormTitle',
      }
    }));
  }

  public onEditCustomerPlantRequest(customerDomainId: number, newCustomerDomainData: ICustomerDomainFormDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.editCustomerDomain',
        successMessageKey: 'credits.editCustomerDomainConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onEditCustomerPlantConfirm(customerDomainId, newCustomerDomainData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onEditCustomerPlantConfirm(customerDomainId: number, newCustomerDomainData: ICustomerDomainFormDTO) {
    const { dispatch, customer } = this.props;
    const { pageSize } = this.state;
    try {
      dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const newCustomerDomainDataEncoded = CustomerPlantDecoder.encodeCustomerPlantFromForm(newCustomerDomainData);
      const customerDomainUpdated = await dispatch<any>(CreditsActions.editCustomerPlant(customerDomainId, newCustomerDomainDataEncoded));
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_CUSTOMER_DOMAIN_MODAL`));
      dispatch<any>(CreditsActions.setSelectedCustomerPlant(customerDomainUpdated));
      await dispatch<any>(CreditsActions.fetchCustomerPlants({ page: 0, pageSize: pageSize, customerId: customer.id }));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      console.log(error)
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_DOMAIN_CREATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.createCustomerDomainError',
        }
      }));
    }
  }

  public onCreateNewCustomerPlantRequest(newCustomerPlantData: ICustomerDomainFormDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'credits.newCustomerDomain',
        successMessageKey: 'credits.customerDomainCreationConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onCreateNewCustomerPlantConfirm(newCustomerPlantData),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`)),
      }
    }));
  }

  public async onCreateNewCustomerPlantConfirm(newCustomerPlantData: ICustomerDomainFormDTO) {
    const { dispatch, customer } = this.props;
    const { pageSize } = this.state;
    try {
      dispatch<any>(ModalsActions.hideModal(`CREATE_CUSTOMER_DOMAIN_CONFIRM_MODAL`));
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      const newCustomerPlantDataEncoded = CustomerPlantDecoder.encodeCustomerPlantFromForm(newCustomerPlantData);
      await dispatch<any>(CreditsActions.createNewCustomerPlant(newCustomerPlantDataEncoded));
      dispatch<any>(ModalsActions.hideModal(`OPERATIONAL_CUSTOMER_DOMAIN_MODAL`));
      await dispatch<any>(CreditsActions.fetchCustomerPlants({ page: 0, pageSize: pageSize, customerId: customer.id }));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_DOMAIN_CREATION`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.createCustomerDomainError',
        }
      }));
    }
  }

  async onFetchCustomerPlantsOnPage (page) {
    const { dispatch, customer } = this.props;
    const { pageSize } = this.state;
    await dispatch<any>(CreditsActions.fetchCustomerPlants({ page, pageSize: pageSize, customerId: customer.id }));
  }

  async onPageSizeChange(pageSize: number) {
    const { dispatch, customer } = this.props;
    this.setState({ pageSize }, async () => {
      await dispatch<any>(CreditsActions.fetchCustomerPlants({ page: 0, pageSize, customerId: customer.id }));
    });
  }

  onDeleteCustomerPlantRequest(customerPlant: IDomainInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(ModalsActions.showModal(`DELETE_CUSTOMER_PLANT_CONFIRM_MODAL`, {
      modalType: ModalTypes.CONFIRM_MODAL,
      modalProps: {
        icon: <WarningIcon style={{ color: 'orange', fontSize: 50 }} />,
        titleMessageKey: 'forms.warning',
        successMessageKey: 'credits.deleteCustomerDomainConfirm',
        confirmMessageKey: 'forms.confirm',
        cancelMessageKey: 'forms.cancel',
        onConfirm: () => this.onDeleteCustomerPlantConfirm(customerPlant),
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`DELETE_CUSTOMER_PLANT_CONFIRM_MODAL`)),
      }
    }));
  }

  async onDeleteCustomerPlantConfirm(customerPlant: IDomainInDTO) {
    const { dispatch } = this.props;
    try {
      dispatch<any>(SettingsActions.setSpinnerVisible(true));
      await dispatch<any>(CreditsActions.deleteCustomerPlant(customerPlant.id));
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.hideModal('DELETE_CUSTOMER_PLANT_CONFIRM_MODAL'));
      this.onFetchCustomerPlantsOnPage(0);
    } catch (error) {
      dispatch<any>(SettingsActions.setSpinnerVisible(false));
      dispatch<any>(ModalsActions.showModal(`ERROR_CUSTOMER_PLANT_DELETE`, {
        modalType: ModalTypes.ERROR_MODAL,
        modalProps: {
          titleMessageKey: 'errors.error',
          errorMessageKey: 'credits.deleteCustomerDomainError',
        }
      }));
    }
  }
  
  public render() {
    const { customerPlants, classes, wallets } = this.props;
    const { pageSize } = this.state;
    
    const canCreateCustomerPlant = AbilityProvider.getAbilityHelper().hasPermission(PERMISSIONS.CUSTOMER_PLANTS_CREATE)

    return (
      <div className={classes.container}>
        <Card className={classes.cardContainer}>
          <div className={classes.header}>
            <div className={classes.headerInner}>
              <DomainsIcon className={classes.icon} />
              <h3 className={classes.title}>{<ReduxLanguage languageKey="credits.customerPlants"/>}</h3>
            </div>
            {canCreateCustomerPlant && 
              <Button
                variant="contained"
                style={{ fontWeight: 'bold', backgroundColor: '#5AC0B1', fontSize: 11, color: 'white' }}
                onClick={() => {this.onOpenNewCustomerPlantForm()}}
              >
                <ReduxLanguage languageKey="forms.createNewCustomer" />
              </Button>
            }
          </div>
          <div className={classes.tableContainer}>
            <CustomerDomainsTable
              rowsPerPage={pageSize}
              customerDomains={customerPlants as PaginatedDataDTO<IDomainInDTO>}
              onRowClick={(customerDomain) => {this.onOpenEditCustomerPlantForm(customerDomain)}}
              onPageSizeChange={pageSize => this.onPageSizeChange(pageSize)}
              onFetchElementsOnPage={page => this.onFetchCustomerPlantsOnPage(page)}
              onDeleteDomainRequest={(customerDomain) => this.onDeleteCustomerPlantRequest(customerDomain)}
              wallets={wallets as PaginatedDataDTO<IWalletInDTO>}
              onShowWalletDetails={wallet => this.props.onShowWalletDetails(wallet)}
            />
          </div>
        </Card>
      </div>
    );
  }
}


function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    vars: state.vars.vars.data,
    userData: state.user.userData,
    selectedCustomerPlant: state.credits.customerPlants.selectedCustomerPlant,
    customerPlants: state.credits.systemCards.plants.data,
    wallets: state.credits.wallets.data,
  };
}

const connector = connect(mapStateToProps);

export default connector(withRouter(withStyles(styles)(CustomerPlantsSection)));